// @ts-nocheck
import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  SimpleChanges,
} from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { CommonModule } from '@angular/common';
import * as turf from '@turf/turf';
import {
  pointsToGeoJSON,
  generateCirclesGeoJSON,
} from '../../utilities/pointsToGeoJSON';
import { sreCategoryPinColor } from '../../utilities/heroReportPinColor';
import { environment } from '../../../environments/environment';
import { HeroReport } from '../../model/hero-report/hero-report.model';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { v4 as uuidv4 } from 'uuid';
import { MapLayer } from '../../model/map-layer/map-layer.service';
import {LngLatBounds} from "mapbox-gl";

@Component({
  selector: 'app-mapbox',
  standalone: true,
  templateUrl: './mapbox.component.html',
  styleUrls: ['./mapbox.component.scss'],
  imports: [
    CommonModule,
    DialogModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
  ],
})
export class MapboxComponent implements OnInit {
  @Input() events: any[] = [];
  @Input() mapLayers: any[] = [];
  @Input() latitude?: number;
  @Input() longitude?: number;
  @Input() mapType: string = 'streets';
  @Input() showEvacuationRadius: boolean = false;
  @Input() isLoading: boolean = true;
  @Input() bounds?: LngLatBounds;

  @Input() createMapLayer: (mapLayer: MapLayer) => Promise<MapLayer>;
  @Input() updateMapLayer: (mapLayer: MapLayer) => Promise<MapLayer>;

  @Output() markerClicked = new EventEmitter<HeroReport>();
  @Output() createHeroReport = new EventEmitter<{
    latitude: number;
    longitude: number;
  }>();
  @Output() createSreReport = new EventEmitter<{
    latitude: number;
    longitude: number;
  }>();
  @Output() mapLoaded = new EventEmitter<void>();

  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef;

  contextMenuVisible = false;
  contextMenuX = 0;
  contextMenuY = 0;
  contextMenuLng = 0;
  contextMenuLat = 0;

  map!: mapboxgl.Map;
  draw!: MapboxDraw;
  accessToken: string = environment.mapboxAccessToken;
  @Input() mapId?: number;

  defaultGutterSpacing = 5;
  defaultLineWidthInMeters = 1;
  defaultGridColor = '#5CE3CD';

  @Output() layerSelected = new EventEmitter<any>();

  private customPolygonLayerIds: string[] = [];
  private customPolygonSourceIds: string[] = [];
  private customGridLayerIds: string[] = [];
  private customGridSourceIds: string[] = [];
  private customMeasureLayerIds: string[] = [];
  private customMeasureSourceIds: string[] = [];
  private markersRefreshScheduled = false;

  // IDs for the “live preview” measurement layer
  private previewSourceId = 'measure-preview-source';
  private previewLayerId = 'measure-preview-layer';

  constructor() {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['events'] && !changes['events'].firstChange && this.map) {
      this.scheduleMarkersRefresh();
    }

    if (
      changes['isLoading'] &&
      !changes['isLoading'].currentValue &&
      !this.map
    ) {
      this.initializeMap([this.longitude, this.latitude]);
    }
    if (!this.map) return;

    if (
      (changes['mapType'] && !changes['mapType'].firstChange) ||
      (changes['showEvacuationRadius'] &&
        !changes['showEvacuationRadius'].firstChange)
    ) {
      const newMapType = changes['mapType']?.currentValue ?? this.mapType;
      const newShowEvacuationRadius =
        changes['showEvacuationRadius']?.currentValue ??
        this.showEvacuationRadius;
      this.changeMap(newMapType, newShowEvacuationRadius);
    }
    if (changes['latitude'] || changes['longitude']) {
      this.map.setCenter([this.longitude, this.latitude]);
    }

    if (changes['mapLayers'] && !changes['mapLayers'].firstChange) {
      this.populateMapLayers();
    }
  }

  private scheduleMarkersRefresh(): void {
    if (!this.map || this.markersRefreshScheduled) {
      return;
    }

    this.markersRefreshScheduled = true;
    setTimeout(() => {
      this.markersRefreshScheduled = false;
      if (this.map) {
        this.addMarkers(this.showEvacuationRadius);
      }
    }, 100);
  }

  private async initializeMap(center: [number, number]): void {
    if (this.bounds) {
      this.map = new mapboxgl.Map({
        accessToken: this.accessToken,
        container: this.mapContainer.nativeElement,
        style: this.getMapStyle(this.mapType),
        bounds: this.bounds,
        fitBoundsOptions: {
          padding: 100
        },
        preserveDrawingBuffer: true
      });
    } else {
      this.map = new mapboxgl.Map({
        accessToken: this.accessToken,
        container: this.mapContainer.nativeElement,
        style: this.getMapStyle(this.mapType),
        center,
        zoom: 10,
        preserveDrawingBuffer: true
      });
    }

    this.map.addControl(
      new mapboxgl.ScaleControl({ maxWidth: 80, unit: 'metric' })
    );

    this.map.on('load', () => {
      this.addMarkers(this.showEvacuationRadius);
      this.addContextMenu();

      const modes = MapboxDraw.modes;
      this.draw = new MapboxDraw({
        modes: {
          draw_polygon: modes.draw_polygon,
          simple_select: modes.simple_select,
          direct_select: modes.direct_select,
        },
        displayControlsDefault: false,
        controls: {},
        styles: this.getDrawStyles(),
      });
      this.map.addControl(this.draw, 'top-left');

      this.map.on('draw.create', (e: any) => {
        for (const f of e.features) {
          const oldId = f.id;
          this.draw.delete([oldId]);

          const newId = uuidv4();
          f.properties = f.properties || {};
          f.id = newId;
          f.properties.featureId = newId;
          this.draw.add(f);

          // Gross Kyle, why are you doing this?
          // I know, I know... Mapbox doesn't return a promise
          // for this.draw.add
          setTimeout(() => {
            this.draw.changeMode('direct_select', { featureId: newId });
          }, 0);

          this.updateGridLayerForPolygon(f);
          this.updateMeasurementLayerForPolygon(f);
          this.clearPreviewMeasurement();

          this.layerSelected.emit({
            id: newId,
            jsonContent: f,
            jsonMetadata: {
              color: f.properties?.gridColor || this.defaultGridColor,
              gutterSpacing:
                f.properties?.gutterSpacing || this.defaultGutterSpacing,
              lineWidth:
                f.properties?.lineWidth || this.defaultLineWidthInMeters,
              showSwimLanes:
                f.properties?.showSwimLanes !== undefined
                  ? f.properties.showSwimLanes
                  : true,
              rotationDegrees: f.properties?.rotationDegrees ?? 0,
            },
          });
        }
      });

      this.map.on('draw.update', (e: any) => {
        for (const f of e.features) {
          if (f.properties) {
            const existingLayer = this.mapLayers.find(
              (layer) => layer.id === f.id
            );
            if (existingLayer) {
              existingLayer.jsonContent.geometry = f.geometry;
              existingLayer.jsonContent = JSON.stringify(f);
              existingLayer.jsonMetadata = JSON.stringify({
                color: f.properties?.gridColor || this.defaultGridColor,
                gutterSpacing:
                  f.properties?.gutterSpacing || this.defaultGutterSpacing,
                lineWidth:
                  f.properties?.lineWidth || this.defaultLineWidthInMeters,
                rotationDegrees: f.properties?.rotationDegrees ?? 0,
                showSwimLanes:
                  f.properties?.showSwimLanes !== undefined
                    ? f.properties.showSwimLanes
                    : true,
              });
              this.updateMapLayer(existingLayer);
            }
            this.updateGridLayerForPolygon(f);
            this.updateMeasurementLayerForPolygon(f);
          }
        }
      });

      this.map.on('draw.modechange', (e: any) => {
        if (e.mode !== 'draw_polygon') {
          this.clearPreviewMeasurement();
        }
      });

      this.map.on('draw.render', () => {
        if (this.draw.getMode() !== 'draw_polygon') {
          this.clearPreviewMeasurement();
          return;
        }

        const all = this.draw.getAll().features;
        if (all.length === 0) {
          this.clearPreviewMeasurement();
          return;
        }

        const f = all[all.length - 1];
        const coords: number[][] = f.geometry.coordinates[0];

        if (coords.length < 2) {
          this.clearPreviewMeasurement();
          return;
        }


        const liveFeatures: GeoJSON.Feature[] = [];
        for (let i = 0; i < coords.length - 1; i++) {
          const [lng1, lat1] = coords[i];
          const [lng2, lat2] = coords[i + 1];
          const ptA = turf.point([lng1, lat1]);
          const ptB = turf.point([lng2, lat2]);
          const distKm = turf.distance(ptA, ptB, { units: 'kilometers' });
          const distMeters = distKm * 1000;
          const text =
            distMeters >= 1000
              ? (distMeters / 1000).toFixed(2) + ' km'
              : distMeters.toFixed(0) + ' m';

          const midpoint = turf.midpoint(ptA, ptB).geometry;
          liveFeatures.push({
            type: 'Feature',
            geometry: midpoint,
            properties: { distance: text },
          });
        }

        const previewGeoJSON: GeoJSON.FeatureCollection = {
          type: 'FeatureCollection',
          features: liveFeatures,
        };
        this.updatePreviewMeasurementLayer(previewGeoJSON);
      });


      this.map.on('draw.selectionchange', (e: any) => {
        if (e.features.length > 0) {
          const mapLayer = this.mapLayers.find(
            (layer) => layer.id === e.features[0].id
          );
          if (mapLayer) {
            this.layerSelected.emit(mapLayer);
            // Ugh, unfortunately, I have to do this again. Basically what is happening
            // is there the above is making the map "rerender" which means the it "unselects"
            // the layer. So we have to reselect it.
            setTimeout(() => {
              this.draw.changeMode('direct_select', {
                featureId: mapLayer.id,
              });
              // Push draw layers to top so highlight remains visible:
              const topDrawIds = [
                'gl-draw-line-active.hot',
                'gl-draw-polygon-stroke-active.hot',
                'gl-draw-polygon-and-line-vertex-active.hot',
                'gl-draw-polygon-and-line-vertex-halo-active.hot',
              ];
              topDrawIds.forEach((id) => {
                if (this.map.getLayer(id)) {
                  this.map.moveLayer(id);
                }
              });
            }, 0);
          }
        }
      });


      this.map.on('zoom', () => {
        const zoom = this.map.getZoom();
        const centerLat = this.map.getCenter().lat;
        const features = this.draw.getAll().features;
        features.forEach((feature: any) => {
          const polygonId = feature.id ? feature.id.toString() : null;
          if (!polygonId) return;
          const layerId = this.getGridLayerId(polygonId);
          const lineWidthInMeters =
            feature.properties?.lineWidth || this.defaultLineWidthInMeters;
          const pixelLineWidth = this.metersToPixels(
            lineWidthInMeters,
            zoom,
            centerLat
          );
          if (this.map.getLayer(layerId)) {
            this.map.setPaintProperty(layerId, 'line-width', pixelLineWidth);
          }
        });
      });

      this.populateMapLayers();
    });

    this.map.on('moveend', () => {
      const c = this.map.getCenter();
      localStorage.setItem('mapCenter', `${c.lng},${c.lat}`);
    });

    await this.untilMapIsLoaded();
    this.mapLoaded.emit()
  }

  private untilMapIsLoaded(): Promise<void> {
    return new Promise(resolve => {
      if (this.map.isStyleLoaded()) {
        resolve();
      } else {
        this.map.on('idle', () => resolve());
      }
    })
  }

  private addContextMenu() {
    this.map.on('contextmenu', (e) => {
      e.preventDefault();
      this.contextMenuLng = e.lngLat.lng;
      this.contextMenuLat = e.lngLat.lat;
      this.contextMenuX = e.point.x;
      this.contextMenuY = e.point.y;
      this.contextMenuVisible = true;
    });
    this.map.on('click', () => (this.contextMenuVisible = false));
  }

  createHeroReportFromCoordinates() {
    this.createHeroReport.emit({
      latitude: this.contextMenuLat,
      longitude: this.contextMenuLng,
    });
    this.contextMenuVisible = false;
  }

  createSreReportFromCoordinates() {
    this.createSreReport.emit({
      latitude: this.contextMenuLat,
      longitude: this.contextMenuLng,
    });
    this.contextMenuVisible = false;
  }

  startPolygonMode(): void {
    this.draw.changeMode('draw_polygon');
  }

  deleteSelectedLayer(layerId: string): void {
    if (!layerId) {
      return;
    }

    const feature = this.draw.get(layerId);

    if (feature) {
      this.draw.delete([layerId]);
      this.removeGridLayerForPolygon(layerId);
    }
  }

  removeGridLayerForPolygon(layerId: string): void {
    if (!layerId) return;

    const featureId = layerId;
    const polygonSourceId = 'polygon-source-' + featureId;
    const polygonLayerId = 'polygon-layer-' + featureId;
    const gridSourceId = this.getGridSourceId(featureId);
    const gridLayerId = this.getGridLayerId(featureId);
    const measureSourceId = this.getMeasureSourceId(featureId);
    const measureLayerId = this.getMeasureLayerId(featureId);

    // Remove layers first, then sources
    if (this.map.getLayer(polygonLayerId)) {
      this.map.removeLayer(polygonLayerId);
    }

    if (this.map.getLayer(gridLayerId)) {
      this.map.removeLayer(gridLayerId);
    }

    if (this.map.getSource(polygonSourceId)) {
      this.map.removeSource(polygonSourceId);
    }

    if (this.map.getSource(gridSourceId)) {
      this.map.removeSource(gridSourceId);
    }

    // Remove the per-edge distance measurement labels for this polygon.
    // These are added as their own symbol layer + geojson source and must be
    // torn down alongside the polygon, otherwise the labels stay on the map
    // after the box is deleted.
    if (this.map.getLayer(measureLayerId)) {
      this.map.removeLayer(measureLayerId);
    }

    if (this.map.getSource(measureSourceId)) {
      this.map.removeSource(measureSourceId);
    }

    // Remove from our tracking arrays regardless of whether the layer was found
    // This ensures we clean up properly even if the layer creation was conditional
    this.customPolygonLayerIds = this.customPolygonLayerIds.filter(id => id !== polygonLayerId);
    this.customPolygonSourceIds = this.customPolygonSourceIds.filter(id => id !== polygonSourceId);
    this.customGridLayerIds = this.customGridLayerIds.filter(id => id !== gridLayerId);
    this.customGridSourceIds = this.customGridSourceIds.filter(id => id !== gridSourceId);
    this.customMeasureLayerIds = this.customMeasureLayerIds.filter(id => id !== measureLayerId);
    this.customMeasureSourceIds = this.customMeasureSourceIds.filter(id => id !== measureSourceId);
  }

  unselectLayer(): void {
    if (this.draw) {
      this.draw.changeMode('simple_select', {});
    }
  }

  private getGridSourceId(polygonId: string): string {
    return `grid-source-${polygonId}`;
  }
  private getGridLayerId(polygonId: string): string {
    return `grid-layer-${polygonId}`;
  }
  private getMeasureSourceId(polygonId: string): string {
    return `measure-source-${polygonId}`;
  }
  private getMeasureLayerId(polygonId: string): string {
    return `measure-layer-${polygonId}`;
  }

  private generateAlignedHorizontalGridLines(
    polygon: turf.Feature<turf.Polygon>,
    rotationDegrees: number,
    spacingMeters: number
  ): turf.FeatureCollection<turf.LineString> {
    const coords = polygon.geometry.coordinates[0];
    if (coords.length < 4) return turf.featureCollection([]);
    const centroid = turf.centroid(polygon).geometry.coordinates;
    const rotatedPolygon = turf.transformRotate(polygon, -rotationDegrees, {
      pivot: centroid,
    });
    const bbox = turf.bbox(rotatedPolygon);
    const baseY = (bbox[1] + bbox[3]) / 2;
    const spacingDeg = spacingMeters / 111111;
    let gridLines: turf.Feature<turf.LineString>[] = [];
    for (let y = baseY; y <= bbox[3]; y += spacingDeg) {
      const fullLine = turf.lineString([
        [bbox[0], y],
        [bbox[2], y],
      ]);
      const clipped = this.clipHorizontalLineToPolygon(
        fullLine,
        rotatedPolygon
      );
      if (clipped) {
        const unrotated = turf.transformRotate(clipped, rotationDegrees, {
          pivot: centroid,
        });
        gridLines.push(unrotated);
      }
    }
    for (let y = baseY - spacingDeg; y >= bbox[1]; y -= spacingDeg) {
      const fullLine = turf.lineString([
        [bbox[0], y],
        [bbox[2], y],
      ]);
      const clipped = this.clipHorizontalLineToPolygon(
        fullLine,
        rotatedPolygon
      );
      if (clipped) {
        const unrotated = turf.transformRotate(clipped, rotationDegrees, {
          pivot: centroid,
        });
        gridLines.push(unrotated);
      }
    }
    return turf.featureCollection(gridLines);
  }

  private clipHorizontalLineToPolygon(
    line: turf.Feature<turf.LineString>,
    polygon: turf.Feature<turf.Polygon>
  ): turf.Feature<turf.LineString> | null {
    let points: turf.Feature<turf.Point>[] = [];
    const intersectFC = turf.lineIntersect(line, polygon);
    points = points.concat(intersectFC.features);
    const startPt = turf.point(line.geometry.coordinates[0]);
    const endPt = turf.point(
      line.geometry.coordinates[line.geometry.coordinates.length - 1]
    );
    if (turf.booleanPointInPolygon(startPt, polygon)) points.push(startPt);
    if (turf.booleanPointInPolygon(endPt, polygon)) points.push(endPt);
    if (points.length < 2) return null;
    points.sort(
      (a, b) => a.geometry.coordinates[0] - b.geometry.coordinates[0]
    );
    return turf.lineSlice(points[0], points[points.length - 1], line);
  }

  private uuidValidate(uuid: string): boolean {
    const regex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return regex.test(uuid);
  }

  public updateGridLayerForPolygon(polygonFeature: any): void {
    if (
      !polygonFeature ||
      polygonFeature.geometry.type !== 'Polygon' ||
      !polygonFeature.id ||
      !this.uuidValidate(polygonFeature.id)
    )
      return;

    const polygon = turf.feature(
      polygonFeature.geometry,
      polygonFeature.properties
    ) as turf.Feature<turf.Polygon>;
    const polygonId = polygonFeature.id.toString();
    const gridColor =
      polygonFeature.properties?.gridColor || this.defaultGridColor;

    const fillLayerId = 'polygon-layer-' + polygonId;
    const gridSourceId = this.getGridSourceId(polygonId);
    const gridLayerId = this.getGridLayerId(polygonId);
    const polygonSourceId = 'polygon-source-' + polygonId;

    if (this.map.getLayer(fillLayerId)) this.map.removeLayer(fillLayerId);
    if (this.map.getLayer(gridLayerId)) this.map.removeLayer(gridLayerId);
    if (this.map.getSource(gridSourceId)) this.map.removeSource(gridSourceId);
    if (this.map.getSource(polygonSourceId))
      this.map.removeSource(polygonSourceId);

    if (!polygonFeature.properties) {
      polygonFeature.properties = {};
    }

    if (polygonFeature.properties.showSwimLanes) {
      const spacing =
        polygonFeature.properties.gutterSpacing || this.defaultGutterSpacing;
      const zoom = this.map.getZoom();
      const centerLat = this.map.getCenter().lat;
      const lineWidthInMeters =
        polygonFeature.properties.lineWidth || this.defaultLineWidthInMeters;
      const pixelLineWidth = this.metersToPixels(
        lineWidthInMeters,
        zoom,
        centerLat
      );
      const rotationDegrees =
        polygonFeature.properties.rotationDegrees || 0;
      const grid = this.generateAlignedHorizontalGridLines(
        polygon,
        rotationDegrees,
        spacing
      );

      this.map.addSource(gridSourceId, {
        type: 'geojson',
        data: grid,
      });

      const drawLayers = this.map
        .getStyle()
        .layers.filter((layer: any) => layer.id === 'points-layer');
      const firstLayer = drawLayers.length > 0 ? drawLayers[0].id : undefined;

      this.map.addLayer(
        {
          id: gridLayerId,
          type: 'line',
          source: gridSourceId,
          paint: {
            'line-color': gridColor,
            'line-width': pixelLineWidth,
          },
        },
        firstLayer
      );
    } else {
      this.map.addSource(polygonSourceId, {
        type: 'geojson',
        data: polygon,
      });

      const drawLayers = this.map
        .getStyle()
        .layers.filter((layer: any) => layer.id === 'points-layer');
      const firstLayer = drawLayers.length > 0 ? drawLayers[0].id : undefined;

      this.map.addLayer(
        {
          id: fillLayerId,
          type: 'fill',
          source: polygonSourceId,
          paint: {
            'fill-color': gridColor,
            'fill-opacity': 0.5,
            'fill-outline-color': gridColor,
          },
        },
        firstLayer
      );
    }
  }

  public updateMeasurementLayerForPolygon(polygonFeature: any): void {
    if (
      !polygonFeature ||
      polygonFeature.geometry.type !== 'Polygon' ||
      !polygonFeature.id ||
      !this.uuidValidate(polygonFeature.id)
    )
      return;

    const polygonId = polygonFeature.id.toString();
    const coords: number[][] = polygonFeature.geometry.coordinates[0];
    if (coords.length < 2) return;

    const features: GeoJSON.Feature[] = [];
    for (let i = 0; i < coords.length - 1; i++) {
      const [lng1, lat1] = coords[i];
      const [lng2, lat2] = coords[i + 1];
      const ptA = turf.point([lng1, lat1]);
      const ptB = turf.point([lng2, lat2]);
      const distKm = turf.distance(ptA, ptB, { units: 'kilometers' });
      const distMeters = distKm * 1000;
      const text =
        distMeters >= 1000
          ? (distMeters / 1000).toFixed(2) + ' km'
          : distMeters.toFixed(0) + ' m';
      const midpoint = turf.midpoint(ptA, ptB).geometry;
      features.push({
        type: 'Feature',
        geometry: midpoint,
        properties: { distance: text },
      });
    }

    const sourceId = this.getMeasureSourceId(polygonId);
    const layerId = this.getMeasureLayerId(polygonId);

    if (this.map.getLayer(layerId)) this.map.removeLayer(layerId);
    if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);

    const geojson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features,
    };

    if (!this.map.isStyleLoaded()) {
      this.map.once('styledata', () => {
        this.addMeasurementLayer(sourceId, layerId, geojson);
      });
    } else {
      this.addMeasurementLayer(sourceId, layerId, geojson);
    }
  }

  private addMeasurementLayer(
    sourceId: string,
    layerId: string,
    geojson: GeoJSON.FeatureCollection
  ) {
    this.map.addSource(sourceId, {
      type: 'geojson',
      data: geojson,
    });

    const drawLayers = this.map.getStyle().layers?.filter((lyr: any) =>
      lyr.id.startsWith('gl-draw-polygon')
    );
    const insertBefore = drawLayers && drawLayers.length ? drawLayers[0].id : undefined;

    this.map.addLayer(
      {
        id: layerId,
        type: 'symbol',
        source: sourceId,
        layout: {
          'text-field': ['get', 'distance'],
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-size': 12,
          'text-offset': [0, 0.5],
        },
        paint: {
          'text-color': '#000000',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1,
        },
      },
      insertBefore
    );

    if (!this.customMeasureSourceIds.includes(sourceId)) {
      this.customMeasureSourceIds.push(sourceId);
    }
    if (!this.customMeasureLayerIds.includes(layerId)) {
      this.customMeasureLayerIds.push(layerId);
    }
  }

  public removeMeasurementLayerForPolygon(polygonFeature: any): void {
    const polygonId = polygonFeature.id?.toString();
    if (!polygonId) return;
    const sourceId = this.getMeasureSourceId(polygonId);
    const layerId = this.getMeasureLayerId(polygonId);
    if (this.map.getLayer(layerId)) this.map.removeLayer(layerId);
    if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);
  }

  private updatePreviewMeasurementLayer(geojson: GeoJSON.FeatureCollection) {

    if (this.map.getLayer(this.previewLayerId)) {
      this.map.removeLayer(this.previewLayerId);
    }
    if (this.map.getSource(this.previewSourceId)) {
      this.map.removeSource(this.previewSourceId);
    }


    this.map.addSource(this.previewSourceId, {
      type: 'geojson',
      data: geojson,
    });


    const drawLayers = this.map.getStyle().layers?.filter((lyr: any) =>
      lyr.id.startsWith('gl-draw-polygon')
    );
    const insertBefore = drawLayers && drawLayers.length ? drawLayers[0].id : undefined;

    this.map.addLayer(
      {
        id: this.previewLayerId,
        type: 'symbol',
        source: this.previewSourceId,
        layout: {
          'text-field': ['get', 'distance'],
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-size': 12,
          'text-offset': [0, 0.5],
        },
        paint: {
          'text-color': '#000000',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1,
        },
      },
      insertBefore
    );
  }

  private clearPreviewMeasurement() {
    if (this.map.getLayer(this.previewLayerId)) {
      this.map.removeLayer(this.previewLayerId);
    }
    if (this.map.getSource(this.previewSourceId)) {
      this.map.removeSource(this.previewSourceId);
    }
  }

  private metersToPixels(
    meters: number,
    zoom: number,
    latitude: number
  ): number {
    return (
      (meters * Math.pow(2, zoom)) /
      (156543.03392 * Math.cos((latitude * Math.PI) / 180))
    );
  }

  changeMap(mapType: string, showEvacuationRadius: boolean): void {
    const style = this.getMapStyle(mapType);
    this.map.once('idle', () => {
      this.addMarkers(showEvacuationRadius);
    });
    this.map.setStyle(style);
  }

  private getMapStyle(mapType: string): string {
    const styles: { [key: string]: string } = {
      streets: 'mapbox://styles/mapbox/streets-v11',
      satellite: 'mapbox://styles/mapbox/satellite-v9',
      terrain: 'mapbox://styles/mapbox/outdoors-v11',
    };
    return styles[mapType] || styles['streets'];
  }

  zoomIn(): void {
    this.map.zoomIn();
  }
  zoomOut(): void {
    this.map.zoomOut();
  }
  flyTo(latitude: number, longitude: number): void {
    this.map.flyTo({
      center: [longitude, latitude],
      zoom: 12,
      essential: true,
    });
  }

  private addMarkers(showEvacuationRadius: boolean): void {
    // Ensure style is loaded before adding markers to avoid race conditions
    if (!this.map.isStyleLoaded()) {
      this.map.once('styledata', () => {
        this.addMarkers(showEvacuationRadius);
      });
      return;
    }

    if (this.map.getLayer('points-layer')) this.map.removeLayer('points-layer');
    if (this.map.getSource('points-source'))
      this.map.removeSource('points-source');
    if (this.map.getLayer('circleFill')) this.map.removeLayer('circleFill');
    if (this.map.getSource('circle-source'))
      this.map.removeSource('circle-source');

    // SRE categories have a fixed pin color that replaces the status color.
    const geoJSONPoints = pointsToGeoJSON(
      this.events.map((event) => {
        const sreColor = sreCategoryPinColor(event.possibleIdentification);
        return sreColor ? { ...event, sreColor } : event;
      }),
    );
    const circlesGeoJSON = generateCirclesGeoJSON(this.events);

    this.map.addSource('points-source', {
      type: 'geojson',
      data: geoJSONPoints,
    });

    this.map.addLayer({
      id: 'points-layer',
      type: 'circle',
      source: 'points-source',
      paint: {
        'circle-radius': 8,
        'circle-color': [
          'case',
          ['has', 'sreColor'],
          ['get', 'sreColor'],
          [
            'match',
            ['get', 'status'],
            'COMPLETED',
            '#41BE8A',
            'PENDING',
            '#E75D4A',
            '#FFFFFF',
          ],
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#FFFFFF',
      },
    });

    // Remove old click handler before adding new one to prevent accumulation
    this.map.off('click', 'points-layer');
    this.map.on('click', 'points-layer', (e) => {
      const feature = e.features && e.features[0];
      const propertyId = feature?.properties?.['id'];
      const event =
        propertyId && this.events.find((ev) => ev['id'] === propertyId);
      if (feature) this.markerClicked.emit(event);
    });

    if (showEvacuationRadius) {
      this.map.addSource('circle-source', {
        type: 'geojson',
        data: circlesGeoJSON,
      });
      this.map.addLayer({
        id: 'circleFill',
        type: 'fill',
        source: 'circle-source',
        paint: {
          'fill-color': 'rgba(231, 93, 74, 0.25)',
          'fill-outline-color': 'rgba(231, 93, 74, 1)',
        },
      });
    }
  }

  private getDrawStyles() {
    return [
      {
        id: 'gl-draw-polygon-fill',
        type: 'fill',
        filter: [
          'all',
          ['==', '$type', 'Polygon'],
          ['!=', 'mode', 'static'],
        ],
        paint: {
          'fill-pattern': 'albumcolors-pattern',
          'fill-opacity': 0.8,
        },
      },
      {
        id: 'gl-draw-polygon-stroke-active',
        type: 'line',
        filter: [
          'all',
          ['==', '$type', 'Polygon'],
          ['==', 'active', 'true'],
        ],
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': '#000',
          'line-width': 1,
          'line-opacity': 1,
        },
      },
      {
        id: 'gl-draw-line',
        type: 'line',
        filter: [
          'all',
          ['==', '$type', 'LineString'],
          ['!=', 'mode', 'static'],
        ],
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': '#000',
          'line-width': 1,
        },
      },
      {
        id: 'gl-draw-line-active',
        type: 'line',
        filter: [
          'all',
          ['==', '$type', 'LineString'],
          ['==', 'active', 'true'],
        ],
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': '#000',
          'line-dasharray': [0.2, 2],
          'line-width': 1,
        },
      },
      {
        id: 'gl-draw-polygon-and-line-vertex-active',
        type: 'circle',
        filter: [
          'all',
          ['==', 'meta', 'vertex'],
          ['==', '$type', 'Point'],
          ['!=', 'mode', 'static'],
        ],
        paint: {
          'circle-radius': 7,
          'circle-color': '#000',
        },
      },
      {
        id: 'gl-draw-polygon-and-line-vertex-halo-active',
        type: 'circle',
        filter: [
          'all',
          ['==', 'meta', 'vertex'],
          ['==', '$type', 'Point'],
          ['!=', 'mode', 'static'],
        ],
        paint: {
          'circle-radius': 5,
          'circle-color': 'white',
        },
      },
    ];
  }

  private populateMapLayers(): void {
    if (!this.map.isStyleLoaded()) {
      this.map.once('styledata', () => {
        this.populateMapLayers();
      });
      return;
    }

    const layers = this.map.getStyle().layers;

    layers.forEach((layer: any) => {
      if (
        layer.id.startsWith('polygon-layer-') ||
        layer.id.startsWith('grid-layer-') ||
        layer.id.startsWith('measure-layer-')
      ) {
        this.map.removeLayer(layer.id);
      }
    });

    const style = this.map.getStyle();
    const sources = Object.keys(style.sources);

    sources.forEach((sourceId: string) => {
      if (
        sourceId.startsWith('polygon-source-') ||
        sourceId.startsWith('grid-source-') ||
        sourceId.startsWith('measure-source-')
      ) {
        this.map.removeSource(sourceId);
      }
    });

    this.customPolygonLayerIds.forEach((id) => {
      if (this.map.getLayer(id)) this.map.removeLayer(id);
    });
    this.customPolygonSourceIds.forEach((id) => {
      if (this.map.getSource(id)) this.map.removeSource(id);
    });
    this.customGridLayerIds.forEach((id) => {
      if (this.map.getLayer(id)) this.map.removeLayer(id);
    });
    this.customGridSourceIds.forEach((id) => {
      if (this.map.getSource(id)) this.map.removeSource(id);
    });
    this.customMeasureLayerIds.forEach((id) => {
      if (this.map.getLayer(id)) this.map.removeLayer(id);
    });
    this.customMeasureSourceIds.forEach((id) => {
      if (this.map.getSource(id)) this.map.removeSource(id);
    });

    this.customPolygonLayerIds = [];
    this.customPolygonSourceIds = [];
    this.customGridLayerIds = [];
    this.customGridSourceIds = [];
    this.customMeasureLayerIds = [];
    this.customMeasureSourceIds = [];

    this.mapLayers.forEach((layer, index) => {
      let feature = layer.jsonContent;
      if (!feature || !feature.geometry) {
        return;
      }

      feature.id = layer.id;

      if (!feature.properties) feature.properties = {};
      feature.properties.featureId = layer.id;
      feature.properties.gridColor = layer.jsonMetadata.color || this.defaultGridColor;
      feature.properties.gutterSpacing = layer.jsonMetadata.gutterSpacing || this.defaultGutterSpacing;
      feature.properties.lineWidth = layer.jsonMetadata.lineWidth || this.defaultLineWidthInMeters;
      feature.properties.showSwimLanes = layer.jsonMetadata.showSwimLanes !== undefined
        ? layer.jsonMetadata.showSwimLanes
        : true;
      feature.properties.rotationDegrees = layer.jsonMetadata.rotationDegrees || 0;

      if (this.draw) {
        const existingFeature = this.draw.get(feature.id);

        if (existingFeature) {
          this.draw.delete([feature.id]);
        }

        this.draw.add(feature);
      }

      const polygonSourceId = 'polygon-source-' + feature.id;
      const polygonLayerId = 'polygon-layer-' + feature.id;
      const gridSourceId = this.getGridSourceId(feature.id.toString());
      const gridLayerId = this.getGridLayerId(feature.id.toString());

      this.customPolygonSourceIds.push(polygonSourceId);
      this.customPolygonLayerIds.push(polygonLayerId);
      this.customGridSourceIds.push(gridSourceId);
      this.customGridLayerIds.push(gridLayerId);

      this.updateGridLayerForPolygon(feature);
      // Re-create the per-edge distance labels so they persist for the
      // lifetime of the layer (across reloads/refetches), not just at draw time.
      this.updateMeasurementLayerForPolygon(feature);
    });
  }
}
